import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Bell, BellOff, Download, Loader2, Share, Smartphone } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@meno/ui/components/ui/card'
import { Button } from '@meno/ui/components/ui/button'
import { Switch } from '@meno/ui/components/ui/switch'
import {
  canPromptInstall, disablePush, enablePush, getPushState, isStandalone,
  needsIOSInstallHint, onInstallAvailabilityChange, promptInstall, sendTestPush,
  type PushState,
} from '@/lib/push'

/**
 * Frame on a phone: install it as an app, and get offline / licence / failed-
 * publish alerts as notifications even when Frame is closed. Per device —
 * every phone or browser turns notifications on for itself.
 */
export default function AppNotificationsCard() {
  const { t } = useTranslation()
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [installable, setInstallable] = useState(canPromptInstall())

  useEffect(() => {
    getPushState().then(setState).catch(() => setState('unsupported'))
    return onInstallAvailabilityChange(() => setInstallable(canPromptInstall()))
  }, [])

  const toggle = async (on: boolean) => {
    setBusy(true)
    try {
      const next = on ? await enablePush() : await disablePush()
      setState(next)
      if (on && next === 'denied') {
        toast.error(t('push.deniedToast', 'Notifications are blocked for Frame. Allow them in your browser settings, then try again.'))
      }
    } catch {
      toast.error(t('push.failed', 'Could not change notifications. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const test = async () => {
    setBusy(true)
    try {
      const sent = await sendTestPush()
      if (sent > 0) toast.success(t('push.testSent', 'Test notification sent.'))
      else toast.error(t('push.testNone', 'No device received it. Turn notifications off and on again.'))
    } catch {
      toast.error(t('push.failed', 'Could not change notifications. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const note: Partial<Record<PushState, string>> = {
    'unsupported': t('push.unsupported', 'This browser can’t show notifications from Frame.'),
    'needs-install': t('push.needsInstall', 'On iPhone and iPad, add Frame to your Home Screen first — notifications work from the installed app.'),
    'unavailable': t('push.unavailable', 'Notifications aren’t available on this server yet.'),
    'denied': t('push.denied', 'Notifications are blocked for Frame in this browser’s settings.'),
  }
  const on = state === 'on'
  const toggleable = state === 'on' || state === 'off'

  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-muted-foreground" />
          {t('push.title', 'App & notifications')}
        </CardTitle>
        <p className="text-muted-foreground text-sm mt-1">
          {t('push.subtitle', 'Install Frame on your phone and get told when a screen goes offline, a licence is about to expire, or a publish didn’t reach every screen — even when Frame is closed.')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Install */}
        {!isStandalone() && (installable || needsIOSInstallHint()) && (
          <div className="rounded-xl border border-border p-3">
            {installable ? (
              <Button className="h-11 w-full gap-2 sm:w-auto" onClick={() => promptInstall()}>
                <Download className="w-4 h-4" />
                {t('push.install', 'Install Frame on this device')}
              </Button>
            ) : (
              <p className="flex items-start gap-2 text-sm">
                <Share className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />
                {t('push.iosInstall', 'To install: tap Share in Safari, then “Add to Home Screen”.')}
              </p>
            )}
          </div>
        )}

        {/* Notifications */}
        <div className="flex min-h-11 items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            {on ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4 text-muted-foreground" />}
            {t('push.toggle', 'Notifications on this device')}
          </div>
          {state === null || busy
            ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            : <Switch checked={on} disabled={!toggleable} onCheckedChange={toggle} />}
        </div>
        {state && note[state] && <p className="text-xs text-muted-foreground">{note[state]}</p>}
        {on && (
          <Button variant="outline" className="h-11" disabled={busy} onClick={test}>
            {t('push.test', 'Send a test notification')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
